import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Card, CardContent } from "@workspace/ui-web/components/ui/card";
import { Badge } from "@workspace/ui-web/components/ui/badge";
import { User, FileText, ScrollText, Download, BadgeCheck } from "lucide-react";
import { Button } from "@workspace/ui-web/components/ui/button";
import { useSeo } from "@/lib/seo";

const bylawsUrl = `${import.meta.env.BASE_URL}bylaws.pdf`;
const irsLetterUrl = `${import.meta.env.BASE_URL}irs-determination-letter.jpg`;

const boardMembers = [
  { name: "Douglas Campbell", role: "President & Treasurer" },
  { name: "Michelle Campbell", role: "Secretary" },
  { name: "Scott Campbell", role: "Board Member" },
];

export default function About() {
  useSeo({
    title: "About Us & Board of Directors | Greater Hope Service Dogs",
    description:
      "Meet the Board of Directors guiding Greater Hope Service Dogs, a non-profit breeding and training service dogs for people in early recovery.",
    path: "/about",
  });
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1">
        {/* Header */}
        <section className="bg-primary text-primary-foreground py-20">
          <div className="container px-4 md:px-8 max-w-4xl mx-auto text-center space-y-4">
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
              About Greater Hope
            </h1>
            <p className="text-lg text-primary-foreground/90 max-w-2xl mx-auto">
              We are a non-profit dedicated to breeding and training service
              dogs for people in early recovery from substance use disorder.
              Meet the people guiding our mission.
            </p>
          </div>
        </section>

        {/* Board of Directors */}
        <section className="py-20 bg-background">
          <div className="container px-4 md:px-8 max-w-5xl mx-auto">
            <div className="text-center max-w-2xl mx-auto mb-12 space-y-3">
              <h2 className="text-3xl font-bold text-foreground">
                Board of Directors
              </h2>
              <p className="text-muted-foreground">
                Our board provides the leadership and oversight that keeps
                Greater Hope accountable to our mission and our community.
              </p>
            </div>

            <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {boardMembers.map((member) => (
                <Card
                  key={member.name}
                  className="text-center hover-elevate transition-all"
                >
                  <CardContent className="pt-8 pb-6 space-y-4">
                    <div className="mx-auto w-24 h-24 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                      <User className="h-10 w-10" />
                    </div>
                    <div className="space-y-1">
                      <h3 className="text-lg font-semibold text-foreground">
                        {member.name}
                      </h3>
                      <p className="text-sm font-medium text-primary">
                        {member.role}
                      </p>
                    </div>
                    <Badge variant="secondary" className="font-normal">
                      Bio &amp; photo coming soon
                    </Badge>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Bylaws */}
        <section className="py-20 bg-accent/30 border-y">
          <div className="container px-4 md:px-8 max-w-3xl mx-auto">
            <div className="text-center max-w-2xl mx-auto mb-8 space-y-3">
              <div className="mx-auto w-14 h-14 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                <ScrollText className="h-7 w-7" />
              </div>
              <h2 className="text-3xl font-bold text-foreground">
                Governing Documents
              </h2>
              <p className="text-muted-foreground">
                Transparency matters to us. Our bylaws govern how Greater Hope
                operates, and our IRS determination letter confirms our
                tax-exempt status.
              </p>
            </div>

            <Card>
              <CardContent className="py-12 text-center space-y-5">
                <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <FileText className="h-6 w-6 text-primary" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-xl font-semibold text-foreground">
                    Bylaws of Greater Hope Service Dogs
                  </h3>
                  <p className="text-muted-foreground max-w-md mx-auto">
                    Read the full bylaws of our nonprofit corporation, covering
                    our board of directors, officers, meetings, and governance.
                  </p>
                </div>
                <div className="flex flex-wrap justify-center gap-3">
                  <Button asChild>
                    <a
                      href={bylawsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      View Bylaws (PDF)
                    </a>
                  </Button>
                  <Button asChild variant="outline">
                    <a href={bylawsUrl} download="greater-hope-bylaws.pdf">
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="mt-8">
              <CardContent className="py-12 text-center space-y-5">
                <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <BadgeCheck className="h-6 w-6 text-primary" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-xl font-semibold text-foreground">
                    IRS 501(c)(3) Determination Letter
                  </h3>
                  <p className="text-muted-foreground max-w-md mx-auto">
                    Greater Hope Service Dogs is recognized by the IRS as a
                    tax-exempt 501(c)(3) public charity. Donations are tax
                    deductible.
                  </p>
                </div>
                <div className="flex flex-wrap justify-center gap-3">
                  <Button asChild>
                    <a
                      href={irsLetterUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      View Determination Letter
                    </a>
                  </Button>
                  <Button asChild variant="outline">
                    <a
                      href={irsLetterUrl}
                      download="greater-hope-irs-determination-letter.jpg"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
